import { useCallback, useState, useEffect, useMemo } from 'react';
import { useElements } from 'react-stripe-js';
import Raven from 'raven-js';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
// @ts-expect-error Untyped module
import getNativeCurrencySymbol from 'I18n/utils/getNativeCurrencySymbol';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { View } from 'foundations-components/transitional/components/View';
import CustomColorButton from './Utilities/CustomColorButton';
import PaymentAuthorizationModal from './AuthorizationModal/PaymentAuthorizationModal';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { getIsRecaptchaEnabled, submitPaymentWithRecaptcha } from '../utils/googleRecaptcha';
import { getIsZeroCheckoutValid } from '../utils/validations';
import { ACH, BACS, CARD, PADS, SEPA } from '../constants/PaymentMethods';
import { getValidPaymentMethods, isACHorSEPA, stripeToHSPaymentMethod } from '../utils/paymentMethods';
import { useSubtotals } from '../context/SubtotalsProvider';
import { useValidationErrors } from '../hooks/useValidationErrors';
import { getCalculateTaxLoading, getTaxCalculationId } from '../state/calculateTaxSlice';
import { setErrorMessages, setShowAllErrors } from '../state/errorMessagesSlice';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ConfirmPaymentButton = ({
  checkoutSession,
  submitEnabled,
  loadingBuyerOverrides,
  selectedOptionalLineItems,
  walletType,
  setShowRequiredFieldsAlert,
  submitObject,
  maximumAmountForPaymentMethod
}) => {
  const elements = useElements();
  const {
    submit,
    loading: submitting,
    error: submitError,
    errorMessage,
    isRecaptchaRetry,
    resetError
  } = submitObject;
  const isTaxCalculationLoading = useSelector(getCalculateTaxLoading);
  const {
    loading: loadingValidationErrors
  } = useValidationErrors();
  const isButtonLoading = submitting || isTaxCalculationLoading;
  const isATPortal = checkoutSession.get('isATPortal');
  const hasCalculateAtCheckoutTaxes = checkoutSession.get('isAutomatedSalesTaxEnabled');
  const taxCalculationId = useSelector(getTaxCalculationId);
  const {
    paymentAmount,
    adjustmentAmounts = []
  } = useSubtotals();
  const checkoutFee = adjustmentAmounts.find(adjustment => adjustment.adjustmentType === 'CHECKOUT_FEE');
  const isZeroCheckout = getIsZeroCheckoutValid(paymentAmount, checkoutSession.get('lineItems').toJS());
  const {
    dispatch,
    formState
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const paymentMethod = formState.get('paymentMethod');
  const shouldUseAuthModal = isACHorSEPA(paymentMethod);
  const [paymentAuthorizationModalOpen, setPaymentAuthorizationModalOpen] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [isFirstRetry, setIsFirstRetry] = useState(true);
  const isRecaptchaEnabled = getIsRecaptchaEnabled(checkoutSession);
  const currencyCode = checkoutSession.get('currencyCode');
  const error = submitError || recaptchaError;
  const paymentMethods = checkoutSession.get('paymentMethods').toJS().map(({
    paymentMethod: method
  }) => method);
  const availablePaymentMethods = maximumAmountForPaymentMethod && getValidPaymentMethods(paymentMethods, paymentAmount, maximumAmountForPaymentMethod).map(stripePaymentMethod => stripeToHSPaymentMethod(stripePaymentMethod));
  useEffect(() => {
    if (errorMessage && errorMessage.props && errorMessage.props.message === 'payments.errorAlert.payment.recaptchaFailed') {
      setRecaptchaError(true);
    }
  }, [errorMessage]);
  const submitPayment = useCallback(recaptchaToken => {
    const resp = submit({
      availablePaymentMethods,
      recaptchaToken,
      setPaymentAuthorizationModalOpen,
      walletType,
      feeType: (checkoutFee === null || checkoutFee === void 0 ? void 0 : checkoutFee.appliesTo) || 'NONE'
    });
    if (resp) {
      resp.catch(err => Raven.captureException(err));
    }
  }, [submit, walletType, availablePaymentMethods, checkoutFee]);
  const submitWithRecaptcha = useCallback(() => {
    dispatch({
      type: 'setIsCheckoutSubmitPending',
      data: {
        value: true
      }
    });
    submitPaymentWithRecaptcha(isATPortal, submitPayment, paymentMethod).catch(err => Raven.captureMessage(`submitPaymentWithRecaptcha failed with error: ${err}`)).finally(() => dispatch({
      type: 'setIsCheckoutSubmitPending',
      data: {
        value: false
      }
    }));
  }, [dispatch, isATPortal, paymentMethod, submitPayment]);
  const submitMethod = () => {
    if (isRecaptchaEnabled) {
      submitWithRecaptcha();
    } else submitPayment();
  };
  const formFieldsValidation = () => {
    if (!elements) return;
    // Trigger form validation and wallet collection
    // https://stripe.com/docs/payments/accept-a-payment-deferred?platform=web&type=payment#submit-the-payment
    elements.submit().then(response => {
      if (response.error) {
        if (response.error.code === 'incomplete') {
          // doesn't set an error if the digital wallet modal is closed without paying
          return;
        }
        setShowRequiredFieldsAlert(true);
        reduxDispatch(setShowAllErrors(true));
        dispatch({
          type: 'setStripeValidationError'
        });
        reduxDispatch(setErrorMessages({
          invalidStripePaymentMethod: {
            isError: true
          }
        }));
        setPaymentAuthorizationModalOpen(false);
        return;
      }
      if (submitEnabled) {
        reduxDispatch(setShowAllErrors(false));
        if (shouldUseAuthModal) {
          setPaymentAuthorizationModalOpen(true);
        } else {
          submitMethod();
        }
      }
    }).catch(err => {
      Raven.captureException(err);
    });
  };
  const onClick = () => {
    setShowRequiredFieldsAlert(true);
    reduxDispatch(setShowAllErrors(true));
    formFieldsValidation();
  };
  useEffect(() => {
    if (isRecaptchaEnabled && isRecaptchaRetry && isFirstRetry) {
      setIsFirstRetry(false);
      resetError();
      submitWithRecaptcha();
    }
  }, [isRecaptchaEnabled, isRecaptchaRetry, isFirstRetry, resetError, submitWithRecaptcha]);
  const getButtonMessage = useCallback(() => {
    if (paymentMethod === PADS) {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "connect-bank-account-message",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.padsPaymentInfo.connectBankAccount"
        })
      });
    } else if (paymentMethod === ACH || paymentMethod === BACS || paymentMethod === SEPA) {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "review-payment-details-message",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.achPaymentInfo.reviewPaymentDetails"
        })
      });
    } else if (!paymentAmount) {
      // this block is reached if all line items are future charges (or one is optional) and the current total is $0
      if (isZeroCheckout) {
        return /*#__PURE__*/_jsx("div", {
          "data-test-id": "schedule-payment-message",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.creditCardPaymentInfo.schedulePayment"
          })
        });
      } else {
        // this block is only reached if all line items in the session are open amount items
        return /*#__PURE__*/_jsx("div", {
          "data-test-id": "open-amount-missing-total-message",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.creditCardPaymentInfo.payOpenAmount",
            options: {
              currencySymbol: getNativeCurrencySymbol(currencyCode),
              currencyCode
            }
          })
        });
      }
    } else if (paymentMethod === CARD && hasCalculateAtCheckoutTaxes && !taxCalculationId) {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "confirm-card-payment-plus-tax-button-message",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "payments.creditCardPaymentInfo.confirmCardPaymentButtonPlusTax_jsx",
          options: {
            value: paymentAmount,
            currencyCode
          },
          elements: {
            Price: FormattedIntlCurrency
          }
        })
      });
    }
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "confirm-card-payment-button-message",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "payments.creditCardPaymentInfo.confirmCardPaymentButton_jsx",
        options: {
          value: paymentAmount,
          currencyCode
        },
        elements: {
          Price: FormattedIntlCurrency
        }
      })
    });
  }, [currencyCode, paymentMethod, isZeroCheckout, paymentAmount, hasCalculateAtCheckoutTaxes, taxCalculationId]);
  const isLoading = useMemo(() => {
    return !shouldUseAuthModal && isButtonLoading;
  }, [shouldUseAuthModal, isButtonLoading]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      marginBlockStart: "600",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(CustomColorButton, {
        style: {
          width: '100%'
        },
        Component: UILoadingButton,
        "data-test-id": "confirm-card-payment-button",
        "data-loading": isLoading // ATs use this to determine if button is interactive
        ,
        type: "submit",
        onClick: onClick,
        loading: isLoading,
        disabled: loadingBuyerOverrides || loadingValidationErrors || recaptchaError,
        failed: shouldUseAuthModal ? true : !!error,
        children: getButtonMessage()
      })
    }), paymentAuthorizationModalOpen && /*#__PURE__*/_jsx(PaymentAuthorizationModal, {
      checkoutSession: checkoutSession.toJS(),
      loading: isButtonLoading,
      error: !!submitError,
      onConfirm: submitMethod,
      onReject: () => {
        setPaymentAuthorizationModalOpen(false);
      },
      selectedOptionalLineItems: selectedOptionalLineItems
    })]
  });
};
export default ConfirmPaymentButton;